/**
 * Payment calculation utilities
 * Based on rent payment workflow documentation
 */

import type { ResidentApplicationData } from '@/types/application'
import type { PaymentConfig, PaymentFee } from '@/types/payment'
import { checkApplicationPaid, getApplicationStatus } from '@/types/application'
import { formatCurrencyInput } from '@/lib/utils/formatters'

/**
 * Calculate remaining amount to be paid
 * Priority: balance_due > invoice amount from payment_adjustment
 * @param applicationData - Application data
 * @returns Remaining amount to pay
 */
export function getRemainingAmount(applicationData: ResidentApplicationData | null): number {
  if (!applicationData) return 0

  // Priority 1: balance_due if positive
  if (applicationData.balance_due && Number(applicationData.balance_due) > 0) {
    return Number(applicationData.balance_due)
  }

  // Priority 2: Invoice amount from payment_adjustment array
  if (applicationData.payment_adjustment && Array.isArray(applicationData.payment_adjustment) && applicationData.payment_adjustment.length > 0) {
    // Sum all adjustment amounts
    const invoiceAmount = applicationData.payment_adjustment.reduce((sum, adjustment) => {
      const amount = adjustment.adjustment_amount ? Number(adjustment.adjustment_amount) : 0
      return sum + (amount > 0 ? amount : 0)
    }, 0)

    if (invoiceAmount > 0) {
      return invoiceAmount
    }
  }

  // Priority 3: Check if there's a default_invoice flag
  if (applicationData.default_invoice) {
    // If default_invoice exists, use balance_due or rent_amount
    const invoiceAmount = applicationData.balance_due || applicationData.rent_amount || 0
    return Number(invoiceAmount) > 0 ? Number(invoiceAmount) : 0
  }

  return 0
}

/**
 * Normalize `/api/config/payments` payloads (unwrap `{ data: … }` when present).
 */
export function normalizePaymentConfigResponse(
  raw: unknown,
): Record<string, unknown> {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {}
  const root = raw as Record<string, unknown>
  const nested = root.data
  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    return nested as Record<string, unknown>
  }
  return root
}

function isGatewayConfig(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === 'object' && !Array.isArray(value))
}

/** Resolve PAYPAL/CARDCONNECT/etc. block from payment config (case-insensitive). */
export function resolvePaymentGatewayConfig(
  paymentConfig: unknown,
  paymentSource: string,
): Record<string, unknown> | null {
  if (!paymentSource) return null
  const map = normalizePaymentConfigResponse(paymentConfig)
  if (!Object.keys(map).length) return null

  const normalizedSource = paymentSource.trim().toLowerCase()
  const direct =
    map[normalizedSource] ??
    map[paymentSource.trim().toUpperCase()] ??
    map[paymentSource.trim()]
  if (isGatewayConfig(direct)) return direct

  for (const [key, entry] of Object.entries(map)) {
    if (key.toLowerCase() === normalizedSource && isGatewayConfig(entry)) {
      return entry
    }
  }
  return null
}

/**
 * Certificate surcharge from payment gateway config (Angular `calculateFee` parity).
 */
export function getCertificateSurchargeFee(
  paymentConfig: PaymentConfig | Record<string, unknown> | null | undefined,
  paymentSource: string,
): number {
  const gateway = resolvePaymentGatewayConfig(paymentConfig, paymentSource)
  if (!gateway) return 0
  const raw = gateway.certificate_surcharge
  if (raw === undefined || raw === null || raw === '') return 0
  const fee = Number(raw)
  return Number.isFinite(fee) ? fee : 0
}

/**
 * Gateway `refund_fee` percent from `/api/config/payments`
 * (Angular `paymentConfig[source].refund_fee` for Charge Refund Fee).
 */
export function getConfiguredRefundFeePercent(
  paymentConfig: PaymentConfig | Record<string, unknown> | null | undefined,
  paymentSource: string,
): number {
  const gateway = resolvePaymentGatewayConfig(paymentConfig, paymentSource)
  if (!gateway) return 0
  const raw = gateway.refund_fee
  if (raw === undefined || raw === null || raw === '') return 0
  const fee = Number(raw)
  return Number.isFinite(fee) ? fee : 0
}

export function formatExternalPaymentSurchargeFee(fee: number): string {
  if (!Number.isFinite(fee) || fee <= 0) return ''
  return formatCurrencyInput(fee, true)
}

/** Angular external_payment CONTRACT: `access_fee - startBalance` (no floor). */
export function computeExternalContractAdjustmentAmount(
  accessFee: number,
  startingBalance: number,
): number {
  const fee = Number(accessFee)
  const balance = Number(startingBalance)
  if (!Number.isFinite(fee) || !Number.isFinite(balance)) return 0
  return fee - balance
}

/**
 * Calculate payment fees for contract/default_invoice payments
 * @param paymentAmount - Payment amount entered by user
 * @param remainingAmount - Remaining amount to pay
 * @param paymentConfig - Payment configuration
 * @param paymentMethod - Selected payment method
 * @param applicationData - Application data
 * @returns Payment fee breakdown
 */
export function calculatePaymentFee(
  paymentAmount: number,
  remainingAmount: number,
  paymentConfig: PaymentConfig | null,
  paymentMethod: string,
  applicationData: ResidentApplicationData | null
): PaymentFee {
  let fee = 0.0
  const editedAmount = paymentAmount

  // Calculate processing fee based on payment method and configuration
  if (
    paymentConfig &&
    paymentMethod &&
    paymentConfig[paymentMethod.toLowerCase() as keyof PaymentConfig]
  ) {
    const methodConfig = paymentConfig[paymentMethod.toLowerCase() as keyof PaymentConfig] as { certificate_surcharge?: number }

    if (methodConfig?.certificate_surcharge) {
      // Check if surcharge should be applied
      const surchargeFee = applicationData?.surcharge_fee
      const applySurcharge = applicationData?.apply_surcharge
      const shouldApplySurcharge =
        !surchargeFee || applySurcharge === '1' || applySurcharge === 1

      if (shouldApplySurcharge) {
        fee = Number(methodConfig.certificate_surcharge)
      }
    }
  }

  const totalAmount = Number((fee + editedAmount).toFixed(2))
  const paidAmount = applicationData?.paid_amount ? Number(applicationData.paid_amount) : 0.0
  const remainingFee = remainingAmount - totalAmount

  return {
    payment_amount: editedAmount,
    processing_fee: fee,
    total_amount: totalAmount,
    paid_amount: paidAmount,
    remaining_fee: remainingFee > 0 ? remainingFee : 0,
  }
}

/**
 * Validate payment amount
 * @param amount - Payment amount to validate
 * @param remainingAmount - Remaining amount to pay
 * @param isPrepaidCard - Whether this is a pre-paid card
 * @returns Error message if invalid, null if valid
 */
export function validatePaymentAmount(
  amount: number,
  remainingAmount: number,
  isPrepaidCard: boolean = false
): string | null {
  if (amount <= 0) {
    return 'Amount must be greater than 0'
  }

  if (amount > remainingAmount) {
    return 'Amount cannot be greater than remaining balance'
  }

  // For pre-paid cards, amount should be less than full rent amount
  if (isPrepaidCard && amount >= remainingAmount) {
    return 'This payment method is intended for cards with limits, and should not be used for the full amount. If you can put the full amount on this card, then turn off the pre-paid card option. Otherwise, reduce the amount to pay on this payment transaction.'
  }

  return null
}

/**
 * Check if payment failed counter indicates frozen account
 * @param applicationData - Application data
 * @returns True if account is frozen
 */
export function isAccountFrozen(applicationData: ResidentApplicationData | null): boolean {
  if (!applicationData) return false

  // Check payment_failed_counter field (may be in different formats)
  const failedCounter = applicationData.payment_failed_counter
  if (failedCounter === undefined || failedCounter === null) return false

  const counter = Number(failedCounter)
  return !isNaN(counter) && counter >= 5
}

/**
 * Get payment type based on application state
 * @param applicationData - Application data
 * @returns Payment type: 'APPLICATION_FEE', 'CONTRACT', or 'DEFAULT_INVOICE'
 */
export function getPaymentType(applicationData: ResidentApplicationData | null): 'APPLICATION_FEE' | 'CONTRACT' | 'DEFAULT_INVOICE' {
  if (!applicationData) return 'APPLICATION_FEE'

  // Check if this is a default invoice payment
  const defaultInvoice = applicationData.default_invoice
  if (defaultInvoice && (defaultInvoice === true || defaultInvoice === '1' || defaultInvoice === 1)) {
    return 'DEFAULT_INVOICE'
  }

  // Check if application fee has been paid using utility function
  const hasApplicationFeePaid = checkApplicationPaid(applicationData)

  // If application fee is paid and application is approved, this is a contract payment
  if (hasApplicationFeePaid) {
    const status = getApplicationStatus(applicationData)
    if (status.toUpperCase() === 'APPROVED') {
      return 'CONTRACT'
    }
  }

  return 'APPLICATION_FEE'
}

/**
 * Check if all required approvals are met for contract/rent payment.
 * Only co-applicants with is_approved_require === '1' whose status is not APPROVED
 * will block the primary applicant from purchasing (mirrors Angular checkAllRequired).
 */
export function checkAllRequired(
  applicationData: ResidentApplicationData | null
): { allRequired: boolean; allRequiredMessage: string } {
  if (!applicationData) return { allRequired: false, allRequiredMessage: '' }

  let allRequired = false
  let allRequiredMessage = ''

  const coResidents = applicationData.co_resident || []
  const coResidentApps = applicationData.co_resident_application || []
  const allCoResidents = [...coResidents, ...coResidentApps]

  for (const element of allCoResidents) {
    if (element.is_approved_require === '1' && element.applicant_status !== 'APPROVED') {
      allRequired = true
      const firstName = element.first_name || ''
      const lastName = element.last_name || ''
      const name = `${firstName} ${lastName}`.trim() || 'Co-applicant'
      allRequiredMessage += `\n${name} requires approval.`
    }
  }

  const primaryApprovalRequired = applicationData.is_approved_require === '1' || applicationData.is_approved_require === 1
  const primaryStatus = getApplicationStatus(applicationData).toUpperCase()
  if (primaryApprovalRequired && primaryStatus !== 'APPROVED' && applicationData.user_data) {
    allRequired = true
    const firstName = applicationData.user_data.first_name || ''
    const lastName = applicationData.user_data.last_name || ''
    allRequiredMessage += `\n${firstName} ${lastName} requires approval.`
  }

  if (allRequired) {
    allRequiredMessage = 'You cannot purchase the certificate yet due to the following reason:' + allRequiredMessage
  }

  return { allRequired, allRequiredMessage }
}

/**
 * Check if application is in a status that must not allow any payment (matches Angular: no purchase when expired/inactive).
 * When true, Purchase button must not be shown and payment must not be processed.
 */
export function isPaymentDisabledByStatus(applicationData: ResidentApplicationData | null): boolean {
  if (!applicationData) return true
  const status = getApplicationStatus(applicationData).toUpperCase()
  const noPaymentStatuses = ['EXPIRED', 'PENDING', 'DENIED', 'CANCELLED']
  return noPaymentStatuses.includes(status)
}

/**
 * Check if purchase button should be shown for application fee
 * @param applicationData - Application data
 * @returns True if purchase button should be shown
 */
export function shouldShowApplicationFeePurchaseButton(
  applicationData: ResidentApplicationData | null
): boolean {
  if (!applicationData) return false

  // Do not show Purchase when status is EXPIRED, PENDING, DENIED, CANCELLED (matches Angular workflow)
  if (isPaymentDisabledByStatus(applicationData)) return false

  const isCancelled =
    applicationData.application_status === 'CANCELLED' ||
    applicationData.applicant_status === 'CANCELLED'

  // Angular: entire addPaymentView section is hidden for CANCELLED
  if (isCancelled) return false

  // Check if application fee invoice exists
  const applicationFeeInvoice = applicationData.application_fee_invoice
  const hasApplicationFeeInvoice = !!applicationFeeInvoice &&
    (applicationFeeInvoice === true || applicationFeeInvoice === '1' || applicationFeeInvoice === 1)

  if (hasApplicationFeeInvoice) {
    return true
  }

  // Check if application fee has not been paid using utility function
  const hasApplicationFeePaid = checkApplicationPaid(applicationData)

  return !hasApplicationFeePaid
}

/**
 * Check if purchase button should be shown for rent payment
 * @param applicationData - Application data
 * @returns Object with show flag and message
 */
export function shouldShowRentPurchaseButton(
  applicationData: ResidentApplicationData | null
): { show: boolean; message: string; disabled: boolean; allRequired: boolean; allRequiredMessage: string } {
  if (!applicationData) {
    return { show: false, message: '', disabled: false, allRequired: false, allRequiredMessage: '' }
  }

  // Do not show Purchase when status is EXPIRED or other no-payment statuses (matches Angular)
  if (isPaymentDisabledByStatus(applicationData)) {
    return { show: false, message: '', disabled: false, allRequired: false, allRequiredMessage: '' }
  }

  // Use utility functions for consistency
  const hasApplicationFeePaid = checkApplicationPaid(applicationData)
  const status = getApplicationStatus(applicationData)
  const normalizedStatus = status.toUpperCase()
  const isApproved = normalizedStatus === 'APPROVED'
  const isCancelled = normalizedStatus === 'CANCELLED'
  const hasApplicationFeeInvoice = !!applicationData.application_fee_invoice

  const rentAdded = applicationData.rent_added
  const hasRentAdded = rentAdded === true || (typeof rentAdded === 'string' && rentAdded === '1') || (typeof rentAdded === 'number' && rentAdded === 1)

  // Check if all required approvals are met (mirrors Angular checkAllRequired)
  const { allRequired, allRequiredMessage } = checkAllRequired(applicationData)

  // Scenario 1: Approved application - No rent payment yet
  if (
    hasApplicationFeePaid &&
    !hasRentAdded &&
    isApproved &&
    !isCancelled
  ) {
    return {
      show: true,
      message: 'You have been Approved. Click the Purchase button to buy your contract now.',
      disabled: hasApplicationFeeInvoice,
      allRequired,
      allRequiredMessage,
    }
  }

  // Scenario 2: Balance due after rent payment (rent increase - Angular: "Your account has been adjusted")
  if (
    hasApplicationFeePaid &&
    hasRentAdded &&
    isApproved &&
    applicationData.balance_due &&
    Number(applicationData.balance_due) > 0 &&
    !isCancelled
  ) {
    const balanceDue = Number(applicationData.balance_due)
    const rentAmount = applicationData.rent_amount != null ? Number(applicationData.rent_amount) : 0
    const rentAmountStr = rentAmount.toFixed(2)
    return {
      show: true,
      message: `Your Access Fee is being increased due to an update from your property regarding the new Rent Amount for $${rentAmountStr}. You owe: $${balanceDue.toFixed(2)}. This must be paid prior to your move in date. Please click the Purchase button to review your payment options.`,
      disabled: hasApplicationFeeInvoice,
      allRequired,
      allRequiredMessage,
    }
  }

  return { show: false, message: '', disabled: false, allRequired: false, allRequiredMessage: '' }
}

/**
 * Check if purchase button should be shown for default invoice
 * @param applicationData - Application data
 * @returns Object with show flag and message
 */
export function shouldShowDefaultInvoicePurchaseButton(
  applicationData: ResidentApplicationData | null
): { show: boolean; message: string; disabled: boolean; allRequired: boolean; allRequiredMessage: string } {
  if (!applicationData) {
    return { show: false, message: '', disabled: false, allRequired: false, allRequiredMessage: '' }
  }

  // Do not show Purchase when status is EXPIRED or other no-payment statuses (matches Angular)
  if (isPaymentDisabledByStatus(applicationData)) {
    return { show: false, message: '', disabled: false, allRequired: false, allRequiredMessage: '' }
  }

  const defaultInvoice = applicationData.default_invoice
  const hasDefaultInvoice = defaultInvoice === true || defaultInvoice === '1' || defaultInvoice === 1

  if (!hasDefaultInvoice) {
    return { show: false, message: '', disabled: false, allRequired: false, allRequiredMessage: '' }
  }

  const balanceDue = applicationData.balance_due ? Number(applicationData.balance_due) : 0
  const applicationFeeInvoice = applicationData.application_fee_invoice
  const hasApplicationFeeInvoice = !!applicationFeeInvoice &&
    (applicationFeeInvoice === true || applicationFeeInvoice === '1' || applicationFeeInvoice === 1)
  const isCancelled =
    applicationData.application_status === 'CANCELLED' ||
    applicationData.applicant_status === 'CANCELLED'

  // Check if all required approvals are met (mirrors Angular checkAllRequired)
  const { allRequired, allRequiredMessage } = checkAllRequired(applicationData)

  if (balanceDue > 0 && !isCancelled) {
    return {
      show: true,
      message: `Your balance due is $${balanceDue.toFixed(2)}. Please click the Purchase button to review your payment options.`,
      disabled: hasApplicationFeeInvoice,
      allRequired,
      allRequiredMessage,
    }
  }

  return { show: false, message: '', disabled: false, allRequired: false, allRequiredMessage: '' }
}

